import {
  getFirestore,
  collection,
  addDoc,
  doc,
  getDoc,
  increment,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  runTransaction,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  where,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { upsertMutualFund } from './userFundsService';

export const MUTUAL_AID_SUGGESTIONS_COLLECTION = 'mutual_aid_suggestions';
export const MUTUAL_AID_VOTES_COLLECTION = 'mutual_aid_votes';
export const MUTUAL_AID_PAGE_COLLECTION = 'system';
export const MUTUAL_AID_PAGE_DOC_ID = 'mutualAidFund';

export function mutualAidPageRef() {
  return doc(getFirestore(), MUTUAL_AID_PAGE_COLLECTION, MUTUAL_AID_PAGE_DOC_ID);
}

/** Page hero wins; otherwise the active fund cover. */
export function resolveFundPageHeroUrl(page, activeFund) {
  const pageUrl = String(page?.heroImageUrl || '').trim();
  if (pageUrl) return pageUrl;
  return String(activeFund?.coverImageUrl || '').trim();
}

/** Remote URL when set; otherwise the bundled placeholder Image source. */
export function fundHeroImageSource(page, activeFund, fallbackSource) {
  const url = resolveFundPageHeroUrl(page, activeFund);
  if (url) return { uri: url };
  return fallbackSource || null;
}

export const SUGGESTION_STATUSES = {
  pending: 'pending',
  approved: 'approved',
  denied: 'denied',
  winner: 'winner',
};

export function normalizeSuggestionStatus(status) {
  const value = String(status || '').trim().toLowerCase();
  if (!value || value === 'open') return SUGGESTION_STATUSES.pending;
  return SUGGESTION_STATUSES[value] || SUGGESTION_STATUSES.pending;
}

export function isVotingOpen(page, now = Date.now()) {
  if (!page?.votingOpen) return false;
  const ends = page.votingEndsAt;
  if (!ends) return true;
  const stamp = Date.parse(ends);
  if (Number.isNaN(stamp)) return true;
  return now < stamp;
}

export function isVotableSuggestion(row, page, now = Date.now()) {
  return normalizeSuggestionStatus(row?.status) === SUGGESTION_STATUSES.approved
    && isVotingOpen(page, now);
}

/** Admin-only preview so we can walk Donate / Vote before live data exists. */
export const ADMIN_FUND_PREVIEW = {
  title: 'September Mutual Aid',
  description: 'Member vote picks the next recipient when this pot hits $1,100.',
  raisedCents: 64000,
  goalCents: 110000,
};

export const ADMIN_VOTE_PREVIEW = [
  {
    id: 'preview-crocodile',
    name: 'The Crocodile',
    title: 'Street-to-stage access ramp',
    description: 'The Crocodile still sends wheelchair patrons around to the loading dock, which is loud, wet, and not a door. This ask finishes a street-to-stage ramp and a short run of handrail so people can enter with the rest of the room. The quote covers fabrication, a licensed install, and a week of temporary access signage while the work happens. When it is done, the same door works for patrons, staff, and visiting crew.',
    link: 'https://www.thecrocodile.com',
    contact: 'access@thecrocodile.com',
    imageUrl: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1200&q=80',
    voteCount: 14,
    status: 'approved',
    preview: true,
  },
  {
    id: 'preview-blanket',
    name: 'Blanket Visuals',
    title: 'Crew medical gap',
    description: 'A Blanket Visuals lighting tech hit an emergency clinic between tour legs and came home with a bill that does not wait for the next settlement. This gap covers the clinic visit, a follow-up, and a few nights of lost work while they recover. The person is still on the crew list and wants to rejoin the next run. Mutual aid here keeps one worker housed and insured enough to stay in the trade.',
    link: 'https://blanketvisuals.com',
    contact: 'care@blanketvisuals.com',
    imageUrl: 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?auto=format&fit=crop&w=1200&q=80',
    voteCount: 11,
    status: 'approved',
    preview: true,
  },
  {
    id: 'preview-fridge',
    name: 'Corridor Community Fridge',
    title: 'A month of restock',
    description: 'The corridor fridge feeds night-shift door staff, touring crews who land hungry, and neighbors who use it in daylight. One month of restock is produce, protein, and the boring staples that actually get taken. Volunteers already run the cleaning and the temperature log; they are short on food, not labor. A full shelf through the next run means people eat without putting it on a per-diem.',
    link: 'https://freedge.org',
    contact: 'fridge@corridorcoop.org',
    imageUrl: 'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=1200&q=80',
    voteCount: 9,
    status: 'approved',
    preview: true,
  },
  {
    id: 'preview-pa',
    name: 'The Hideout',
    title: 'Mains amp replacement',
    description: 'The Hideout’s mains amp failed mid-week and the room has been borrowing a noisy spare that will not last a fall calendar. Replacing it keeps a 200-cap independent room open for the shows already posted. The quote is a working used mains plus install and a spare cable loom so the next failure is not another dark weekend. No PA, no door, no pay for the bands on the books.',
    link: 'https://hideoutchicago.com',
    contact: 'bookings@hideoutchicago.com',
    imageUrl: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&w=1200&q=80',
    voteCount: 7,
    status: 'approved',
    preview: true,
  },
  {
    id: 'preview-hear',
    name: 'Door Staff Collective',
    title: 'Hearing-protection kits',
    description: 'Door staff and openers at three DIY rooms are still working without hearing protection that fits a long shift. These kits are rated plugs, a simple fit guide, and a one-page card on breaks and volume so people can keep the job. The collective already has a volunteer to hand them out on load-in. The point is not merch; it is keeping people’s ears through a season of loud rooms.',
    link: 'https://hearinghealthfoundation.org',
    contact: 'kits@doorstaffcollective.org',
    imageUrl: 'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?auto=format&fit=crop&w=1200&q=80',
    voteCount: 6,
    status: 'approved',
    preview: true,
  },
  {
    id: 'preview-van',
    name: 'Shared Tour Van',
    title: 'Roadside breakdown fund',
    description: 'The shared tour van threw a belt on a shoulder and left two bands and a tech stranded between dates. This fund covers the tow, the parts, and a mechanic who can turn it around before the next hop. The van is how this corridor moves gear without renting a new sprinter every week. Getting it back on the road keeps the remaining dates, and the people on them, paid.',
    link: 'https://venuereportcard.com',
    contact: 'van@sharedtour.org',
    imageUrl: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1200&q=80',
    voteCount: 4,
    status: 'approved',
    preview: true,
  },
];

export const SUGGESTION_FIELD_KEYS = ['name', 'title', 'description', 'link', 'contact', 'imageUrl'];

/** Same fields a member submits on Suggest a Donee. */
export function suggestionDisplayFields(row = {}) {
  const name = String(row?.name || '').trim();
  const title = String(row?.title || '').trim();
  return {
    name,
    title: title || name,
    description: String(row?.description || '').trim(),
    link: String(row?.link || '').trim(),
    contact: String(row?.contact || '').trim(),
    imageUrl: String(row?.imageUrl || '').trim(),
  };
}

export function visibleActiveFund(activeFund, isAdmin = false) {
  if (activeFund) return { fund: activeFund, preview: false };
  if (isAdmin) return { fund: ADMIN_FUND_PREVIEW, preview: true };
  return { fund: null, preview: false };
}

export function visibleVoteSuggestions({ suggestions = [], page = {}, isAdmin = false, now = Date.now() } = {}) {
  const live = (suggestions || []).filter((row) => isVotableSuggestion(row, page, now));
  if (live.length) return { rows: live, preview: false };
  if (isAdmin) return { rows: ADMIN_VOTE_PREVIEW, preview: true };
  return { rows: [], preview: false };
}

export const MUTUAL_AID_GOAL_CENTS = 110000;

export function fundPotProgress({ funds = [], isAdmin = false } = {}) {
  const raisedCents = (funds || []).reduce((sum, fund) => (
    sum + Math.max(0, Math.round(Number(fund?.raisedCents) || 0))
  ), 0);
  if (raisedCents > 0) {
    return { raisedCents, goalCents: MUTUAL_AID_GOAL_CENTS, preview: false };
  }
  if (isAdmin) {
    return {
      raisedCents: ADMIN_FUND_PREVIEW.raisedCents,
      goalCents: MUTUAL_AID_GOAL_CENTS,
      preview: true,
    };
  }
  return { raisedCents: 0, goalCents: MUTUAL_AID_GOAL_CENTS, preview: false };
}

export function homeFundCardFromFund(fund = {}) {
  const title = String(fund.title || fund.name || '').trim();
  return {
    id: fund.id || title,
    title,
    name: String(fund.name || title).trim(),
    description: String(fund.description || '').trim(),
    imageUrl: String(fund.coverImageUrl || fund.imageUrl || '').trim(),
    raisedCents: Math.max(0, Math.round(Number(fund.raisedCents) || 0)),
    goalCents: Math.max(0, Math.round(Number(fund.goalCents) || 0)),
  };
}

export function homeFundCardFromSuggestion(row = {}) {
  const fields = suggestionDisplayFields(row);
  return {
    id: row.id,
    title: fields.title,
    name: fields.name,
    description: fields.description,
    imageUrl: fields.imageUrl,
    raisedCents: 0,
    goalCents: 0,
  };
}

/** Live mutual funds when present; otherwise the current vote slate. */
export function visibleHomeFunds({ funds = [], votable = [] } = {}) {
  const live = (funds || []).filter((fund) => fund && (fund.title || fund.name));
  if (live.length) return { rows: live.map(homeFundCardFromFund), preview: false };
  if (votable.length) {
    return {
      rows: votable.map(homeFundCardFromSuggestion),
      preview: votable.every((row) => row.preview),
    };
  }
  return { rows: [], preview: false };
}

export const MUTUAL_AID_VOTES_PER_USER = 3;

export function remainingMutualAidVotes(usedCount, limit = MUTUAL_AID_VOTES_PER_USER) {
  const used = Math.max(0, Math.round(Number(usedCount) || 0));
  const cap = Math.max(0, Math.round(Number(limit) || 0));
  return Math.max(0, cap - used);
}

export function summarizeMutualAidAdmin({ suggestions = [], funds = [], page = {} } = {}) {
  const counts = { pending: 0, approved: 0, denied: 0, winner: 0 };
  let voteTotal = 0;
  suggestions.forEach((row) => {
    const status = normalizeSuggestionStatus(row?.status);
    if (counts[status] != null) counts[status] += 1;
    voteTotal += Number(row?.voteCount) || 0;
  });
  const raisedCents = funds.reduce((sum, fund) => sum + (Number(fund?.raisedCents) || 0), 0);
  return {
    ...counts,
    suggestionCount: suggestions.length,
    voteTotal,
    raisedCents,
    votingOpen: isVotingOpen(page),
    votingEndsAt: page?.votingEndsAt || '',
  };
}

export function mutualAidInfoLines({ includeGuidelines = false } = {}) {
  const lines = [...MUTUAL_AID_COPY.lines];
  if (includeGuidelines) {
    lines.push(MUTUAL_AID_COPY.guidelinesIntro, ...MUTUAL_AID_COPY.guidelines);
  }
  return lines;
}

const DONEE_GUIDELINES = [
  'DIY or Independent Live Music Venue or Artist, Crew or Employee support.',
  'Accessibility enhancement inside or outside live music venues.',
  'Educational resources related to live music.',
];

export const MUTUAL_AID_COPY = {
  title: 'MUTUAL AID FUND',
  subtitle: '11% of VRC net income goes here. When the fund hits $1,100, members vote on the next recipient.',
  lines: [
    'Every month, 11% of all VRC net income goes into the Mutual Aid Fund.',
    'Every time the Fund reaches $1100, it is donated and the next Fund Recipient is selected by member vote.',
    'As a member of VRC you can suggest a fund Donee and vote on fund proposals.',
    'Anyone can donate.',
  ],
  guidelinesIntro: 'Please keep Donee suggestions related to the following:',
  guidelines: DONEE_GUIDELINES,
  donateMailto:
    'mailto:contact@venuereportcard.com?subject=Donate%20to%20VRC%20Mutual%20Aid%20Fund',
};

function suggestionsCol() {
  return collection(getFirestore(), MUTUAL_AID_SUGGESTIONS_COLLECTION);
}

export function subscribeMutualAidSuggestions(onData, onError) {
  return onSnapshot(
    query(suggestionsCol(), orderBy('createdAt', 'desc')),
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => (Number(b.voteCount) || 0) - (Number(a.voteCount) || 0));
      onData?.(rows);
    },
    onError,
  );
}

export function subscribeMutualAidPage(onData, onError) {
  return onSnapshot(
    mutualAidPageRef(),
    (snap) => {
      onData?.(snap.exists() ? snap.data() : {});
    },
    onError,
  );
}

export async function uploadFundPageHero(file) {
  if (!file) return '';
  const safeName = String(file.name || 'hero.jpg').replace(/[^\w.\-]+/g, '_');
  const path = `mutual_funds/covers/page_${Date.now()}_${safeName}`;
  const storageRef = ref(getStorage(), path);
  await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
  return getDownloadURL(storageRef);
}

export async function saveMutualAidPageHero(heroImageUrl) {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('Sign in to update the Fund page photo.');
  await setDoc(mutualAidPageRef(), {
    heroImageUrl: String(heroImageUrl || '').trim(),
    updatedAt: serverTimestamp(),
    updatedBy: auth.currentUser.uid,
  }, { merge: true });
}

export async function saveMutualAidVotingPeriod({ votingOpen, votingEndsAt }) {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('Sign in to update voting.');
  await setDoc(mutualAidPageRef(), {
    votingOpen: Boolean(votingOpen),
    votingEndsAt: String(votingEndsAt || '').trim(),
    updatedAt: serverTimestamp(),
    updatedBy: auth.currentUser.uid,
  }, { merge: true });
}

export async function setMutualAidSuggestionStatus(id, status) {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('Sign in as admin.');
  const sid = String(id || '').trim();
  if (!sid) throw new Error('Missing suggestion.');
  await updateDoc(doc(getFirestore(), MUTUAL_AID_SUGGESTIONS_COLLECTION, sid), {
    status: normalizeSuggestionStatus(status),
    reviewedAt: serverTimestamp(),
    reviewedBy: auth.currentUser.uid,
  });
}

export async function selectMutualAidWinner(suggestionId) {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('Sign in as admin.');
  const sid = String(suggestionId || '').trim();
  if (!sid) throw new Error('Missing suggestion.');
  const suggestionRef = doc(getFirestore(), MUTUAL_AID_SUGGESTIONS_COLLECTION, sid);
  const snap = await getDoc(suggestionRef);
  if (!snap.exists()) throw new Error('That suggestion is gone.');
  const row = snap.data() || {};
  const fields = suggestionDisplayFields(row);
  await updateDoc(suggestionRef, {
    status: SUGGESTION_STATUSES.winner,
    reviewedAt: serverTimestamp(),
    reviewedBy: auth.currentUser.uid,
  });
  await setDoc(mutualAidPageRef(), {
    votingOpen: false,
    winnerId: sid,
    updatedAt: serverTimestamp(),
    updatedBy: auth.currentUser.uid,
  }, { merge: true });
  await upsertMutualFund({
    title: fields.title,
    description: fields.description,
    coverImageUrl: fields.imageUrl || null,
    goalCents: 110000,
    active: true,
  });
}

export async function uploadDoneeImage(file) {
  if (!file) return '';
  const auth = getAuth();
  const uid = auth.currentUser?.uid || 'anon';
  const safeName = String(file.name || 'donee.jpg').replace(/[^\w.\-]+/g, '_');
  const path = `mutual_aid/suggestions/${uid}/${Date.now()}_${safeName}`;
  const storageRef = ref(getStorage(), path);
  await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
  return getDownloadURL(storageRef);
}

export async function submitMutualAidSuggestion({
  name,
  title,
  link,
  contact,
  description,
  imageUrl,
}) {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('Sign in to suggest a donee.');
  const fields = suggestionDisplayFields({ name, title, link, contact, description, imageUrl });
  if (!fields.name) throw new Error('Name is required.');
  const refDoc = await addDoc(suggestionsCol(), {
    name: fields.name,
    title: fields.title,
    link: fields.link,
    contact: fields.contact,
    description: fields.description,
    imageUrl: fields.imageUrl,
    userId: auth.currentUser.uid,
    userEmail: auth.currentUser.email || '',
    voteCount: 0,
    status: SUGGESTION_STATUSES.pending,
    createdAt: serverTimestamp(),
  });
  return refDoc.id;
}

export function voteDocId(uid, suggestionId) {
  return `${uid}_${suggestionId}`;
}

export async function voteMutualAidSuggestion(suggestionId) {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('Sign in to vote.');
  const uid = auth.currentUser.uid;
  const sid = String(suggestionId || '').trim();
  if (!sid) throw new Error('Missing proposal.');
  const db = getFirestore();
  const voteRef = doc(db, MUTUAL_AID_VOTES_COLLECTION, voteDocId(uid, sid));
  const suggestionRef = doc(db, MUTUAL_AID_SUGGESTIONS_COLLECTION, sid);
  const mineSnap = await getDocs(query(
    collection(db, MUTUAL_AID_VOTES_COLLECTION),
    where('userId', '==', uid),
  ));
  const mineRefs = mineSnap.docs.map((row) => row.ref);

  await runTransaction(db, async (tx) => {
    const existing = await tx.get(voteRef);
    if (existing.exists()) throw new Error('You already voted for this proposal.');
    let used = 0;
    for (const refDoc of mineRefs) {
      const snap = await tx.get(refDoc);
      if (snap.exists()) used += 1;
    }
    if (used >= MUTUAL_AID_VOTES_PER_USER) {
      throw new Error(`You already used all ${MUTUAL_AID_VOTES_PER_USER} votes. Unvote one to vote again.`);
    }
    const suggestion = await tx.get(suggestionRef);
    if (!suggestion.exists()) throw new Error('That proposal is no longer available.');
    const pageSnap = await tx.get(mutualAidPageRef());
    const page = pageSnap.exists() ? pageSnap.data() : {};
    if (!isVotableSuggestion({ ...suggestion.data(), id: sid }, page)) {
      throw new Error('Voting is closed or this donee is not in the current vote.');
    }
    tx.set(voteRef, {
      userId: uid,
      suggestionId: sid,
      createdAt: serverTimestamp(),
    });
    tx.update(suggestionRef, { voteCount: increment(1) });
  });
}

export async function unvoteMutualAidSuggestion(suggestionId) {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('Sign in to unvote.');
  const uid = auth.currentUser.uid;
  const sid = String(suggestionId || '').trim();
  if (!sid) throw new Error('Missing proposal.');
  const db = getFirestore();
  const voteRef = doc(db, MUTUAL_AID_VOTES_COLLECTION, voteDocId(uid, sid));
  const suggestionRef = doc(db, MUTUAL_AID_SUGGESTIONS_COLLECTION, sid);

  await runTransaction(db, async (tx) => {
    const existing = await tx.get(voteRef);
    if (!existing.exists()) throw new Error('You have not voted for this proposal.');
    const suggestion = await tx.get(suggestionRef);
    tx.delete(voteRef);
    if (suggestion.exists()) {
      tx.update(suggestionRef, { voteCount: increment(-1) });
    }
  });
}

export async function hasVotedForSuggestion(uid, suggestionId) {
  if (!uid || !suggestionId) return false;
  const snap = await getDoc(
    doc(getFirestore(), MUTUAL_AID_VOTES_COLLECTION, voteDocId(uid, suggestionId)),
  );
  return snap.exists();
}

export async function loadVotedSuggestionIds(uid, suggestionIds) {
  if (!uid) return new Set();
  const ids = Array.isArray(suggestionIds) ? suggestionIds.filter(Boolean) : [];
  const voted = new Set();
  await Promise.all(ids.map(async (sid) => {
    if (await hasVotedForSuggestion(uid, sid)) voted.add(sid);
  }));
  return voted;
}
